'use client';

import React, { useState, useEffect } from 'react';
import { X, Bell, CheckCircle, Mail, Phone } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { upsertPriceAlert, removePriceAlert, getPriceAlertPackageIds } from '@/lib/services/patientDataService';

interface PriceAlertModalProps {
  packageId: string;
  packageName: string;
  currentPrice: number;
  onClose: () => void;
}

export default function PriceAlertModal({ packageId, packageName, currentPrice, onClose }: PriceAlertModalProps) {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [contactType, setContactType] = useState<'email' | 'phone'>('email');
  const [submitted, setSubmitted] = useState(false);
  const [alreadySet, setAlreadySet] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.id) {
      getPriceAlertPackageIds(user.id).then((ids) => {
        if (ids.includes(packageId)) setAlreadySet(true);
      });
    } else {
      const alerts: Record<string, unknown> = JSON.parse(localStorage.getItem('priceAlerts') || '{}');
      if (alerts[packageId]) setAlreadySet(true);
    }
  }, [packageId, user?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contact.trim()) return;
    setLoading(true);

    try {
      if (user?.id) {
        await upsertPriceAlert(user.id, packageId, packageName, currentPrice, contact.trim(), contactType);
      } else {
        // localStorage fallback for guests
        const alerts: Record<string, unknown> = JSON.parse(localStorage.getItem('priceAlerts') || '{}');
        alerts[packageId] = { packageId, packageName, currentPrice, name: name.trim(), contact: contact.trim(), contactType, createdAt: new Date().toISOString() };
        localStorage.setItem('priceAlerts', JSON.stringify(alerts));
      }
      setSubmitted(true);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAlert = async () => {
    setLoading(true);
    try {
      if (user?.id) {
        await removePriceAlert(user.id, packageId);
      } else {
        const alerts: Record<string, unknown> = JSON.parse(localStorage.getItem('priceAlerts') || '{}');
        delete alerts[packageId];
        localStorage.setItem('priceAlerts', JSON.stringify(alerts));
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 backdrop-blur-sm px-4">
      <div className="bg-card w-full max-w-md rounded-2xl shadow-modal overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-amber-50">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
              <Bell size={16} className="text-amber-600" />
            </div>
            <div>
              <h3 className="font-bold text-base text-foreground">Price Alert</h3>
              <p className="text-xs text-muted-foreground">Get notified when price drops</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5">
          {/* Package info */}
          <div className="bg-secondary rounded-xl px-4 py-3 mb-5">
            <p className="text-xs text-muted-foreground mb-0.5">Package</p>
            <p className="font-bold text-foreground text-sm">{packageName}</p>
            <p className="text-primary font-extrabold text-lg stats-number">
              ₹{currentPrice.toLocaleString('en-IN')}
              <span className="text-xs text-muted-foreground font-normal ml-1">current price</span>
            </p>
          </div>

          {submitted || alreadySet ? (
            <div className="text-center py-4">
              {submitted ? (
                <>
                  <CheckCircle size={40} className="text-green-500 mx-auto mb-3" />
                  <h4 className="font-bold text-foreground mb-1">Alert Set Successfully!</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    We&apos;ll notify you at <span className="font-semibold text-foreground">{contact}</span> when{' '}
                    <span className="font-semibold text-primary">{packageName}</span> goes on a special discount.
                  </p>
                </>
              ) : (
                <>
                  <Bell size={40} className="text-amber-500 mx-auto mb-3" />
                  <h4 className="font-bold text-foreground mb-1">Alert Already Active</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    You already have a price alert set for <span className="font-semibold text-primary">{packageName}</span>.
                  </p>
                  <button
                    onClick={handleRemoveAlert}
                    disabled={loading}
                    className="text-sm text-danger hover:underline mb-4 block mx-auto disabled:opacity-50"
                  >
                    Remove this alert
                  </button>
                </>
              )}
              <button onClick={onClose} className="btn-primary text-sm py-2.5 px-6">Done</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {!user && (
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">
                    Your Name <span className="text-muted-foreground font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter your name"
                    className="input-field"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Notify me via</label>
                <div className="flex gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => setContactType('email')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      contactType === 'email' ? 'border-primary bg-secondary text-primary' : 'border-border text-muted-foreground hover:border-primary'
                    }`}
                  >
                    <Mail size={12} /> Email
                  </button>
                  <button
                    type="button"
                    onClick={() => setContactType('phone')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      contactType === 'phone' ? 'border-primary bg-secondary text-primary' : 'border-border text-muted-foreground hover:border-primary'
                    }`}
                  >
                    <Phone size={12} /> WhatsApp / SMS
                  </button>
                </div>
                <input
                  type={contactType === 'email' ? 'email' : 'tel'}
                  placeholder={contactType === 'email' ? 'your@email.com' : '+91 98765 43210'}
                  className="input-field"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  required
                />
              </div>

              <p className="text-xs text-muted-foreground">
                We&apos;ll send you a one-time notification when this package price drops. No spam, ever.
              </p>

              <button type="submit" disabled={loading} className="btn-primary w-full py-2.5 text-sm disabled:opacity-60">
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Saving…
                  </span>
                ) : (
                  <><Bell size={14} className="mr-2" />Set Price Alert</>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
